import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Autocomplete as MuiAutocomplete, Stack} from '@mui/material';
import type {
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import compact from 'lodash/compact';
import debounce from 'lodash/debounce';
import type {CurrentFilterType} from 'api/types';
import type {ThesaurusId} from 'services/thesaurus/types';
import {useThesaurus} from 'services/thesaurus';
import {TextField} from 'components';
import type {AutocompleteOption, NodeMapper} from '../../types';
import {canFreeSolo, findThesaurusNode, searchThesaurus} from '../../functions';

type Props = {
  thesaurusId: ThesaurusId;
  filter: CurrentFilterType;
  onChange: (filter: CurrentFilterType) => void;
  chipLabel?: keyof AutocompleteOption;
};

export const FilterThesaurusValue: React.FC<Props> = ({
  thesaurusId,
  filter,
  onChange,
  chipLabel = 'id',
}) => {
  const thesaurus = useThesaurus(thesaurusId);
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = React.useState<AutocompleteOption[]>([]);

  const nodeMapper: NodeMapper = useCallback(
    node => ({
      ...node,
      id: node.code, // Use code as id by default
    }),
    [],
  );

  const handleInputChange = useCallback((_: unknown, newValue: string) => {
    setInputValue(newValue);
  }, []);

  const values: AutocompleteOption[] = useMemo(() => {
    const nodes = (filter.values ?? []).map(v =>
      findThesaurusNode(v, thesaurus),
    );
    return compact(nodes ?? []).map(nodeMapper);
  }, [filter.values, nodeMapper, thesaurus]);

  const freeSolo = useMemo(() => {
    return canFreeSolo(filter);
  }, [filter]);

  const handleOnChange = useCallback(
    (
      _: React.SyntheticEvent,
      value:
        | AutocompleteOption
        | (AutocompleteOption | string)[]
        | string
        | null,
    ) => {
      const values = (
        value === null ? [] : Array.isArray(value) ? value : [value]
      )
        .filter(v => !(!freeSolo && typeof v === 'string'))
        .map(v => (typeof v === 'string' ? v : v.id));

      onChange({
        ...filter,
        values,
      });
    },
    [filter, freeSolo, onChange],
  );

  const fetch = useMemo(
    () =>
      debounce(
        (input: string, callback: (results: AutocompleteOption[]) => void) => {
          callback(searchThesaurus(input, thesaurus, nodeMapper));
        },
        200,
      ),
    [nodeMapper, thesaurus],
  );

  const removeDuplicates = useCallback((options: AutocompleteOption[]) => {
    return options.filter(
      (value, index, self) => index === self.findIndex(t => t.id === value.id),
    );
  }, []);

  useEffect(() => {
    if (inputValue.length > 0) {
      fetch(inputValue, options => {
        setOptions(removeDuplicates(options));
      });
    }
  }, [inputValue, fetch, removeDuplicates]);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        type={'text'}
        name={filter.name}
        layout={{flex: 1}}
        {...params}
      />
    ),
    [filter.name],
  );

  const renderTags = useCallback(
    (
      value: Array<AutocompleteOption>,
      getTagProps: AutocompleteRenderGetTagProps,
    ) => {
      return (
        <Stack
          direction={'row'}
          flexWrap={'wrap'}
          gap={0.5}
          width={'inherit'}
          overflow={'hidden'}>
          {value.map((option: AutocompleteOption, index: number) => (
            <Chip
              label={option[chipLabel]}
              size={'small'}
              {...getTagProps({index})}
              key={option.id}
            />
          ))}
        </Stack>
      );
    },
    [chipLabel],
  );

  return (
    <MuiAutocomplete
      multiple={true}
      freeSolo={freeSolo}
      value={values}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={v =>
        typeof v === 'string'
          ? v
          : `${chipLabel === 'id' ? `${v.id}: ` : ''}${v.label}`
      }
      onChange={handleOnChange}
      filterOptions={x => x} // Tells MUI not to filter internally
      noOptionsText={'Ingen treff'}
      isOptionEqualToValue={(o1, o2) => o1.id === o2.id}
      size={'small'}
      renderTags={renderTags}
      renderInput={renderInput}
    />
  );
};
